import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// Matches Common/Enums/ApprovalStatus.cs
export type ApprovalStatus = 0 | 1 | 2 | 3; // Draft, Pending, Approved, Rejected

// Matches Common/Enums/AgroforestryReceiptProvenance.cs
export const PROVENANCE = {
  ExternalPurchase: 0,
  InHousePropagation: 1,
} as const;
export type AgroforestryReceiptProvenance =
  (typeof PROVENANCE)[keyof typeof PROVENANCE];

export const provenanceLabel = (p: AgroforestryReceiptProvenance) =>
  p === PROVENANCE.InHousePropagation ? "In-house" : "External";

export interface CreateAgroforestryReceiptLineDto {
  cropTypeId: string;
  cropVarietyId: string;
  quantity: number;
  unitCost?: number;
}

export interface CreateAgroforestryReceiptDto {
  provenance: AgroforestryReceiptProvenance;
  receivedDate: string;
  supplierName?: string;
  paymentReference?: string;
  notes?: string;
  lines: CreateAgroforestryReceiptLineDto[];
}

export interface ApproveAgroforestryReceiptDto {
  isApproved: boolean;
  rejectionReason?: string;
}

export interface AgroforestryReceiptLineDto {
  id: string;
  cropTypeId: string;
  cropTypeName: string;
  cropVarietyId: string;
  cropVarietyName: string;
  quantity: number;
  unitCost?: number;
  stockEntryId?: string;
}

export interface AgroforestryReceiptDto {
  id: string;
  receiptNumber: string;
  provenance: AgroforestryReceiptProvenance;
  provenanceName: string;
  receivedDate: string;
  supplierName?: string;
  paymentReference?: string;
  notes?: string;
  approvalStatus: ApprovalStatus;
  approvalStatusName: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  approvedByUserId?: string;
  approvedByUserName?: string;
  approvedAt?: string;
  rejectionReason?: string;
  totalQuantity: number;
  totalCost?: number;
  lines: AgroforestryReceiptLineDto[];
}

export interface AgroforestryStockEntryDto {
  id: string;
  cropTypeId: string;
  cropTypeName: string;
  variety?: string;
  totalQuantity: number;
  reservedQuantity: number;
  dispatchedQuantity: number;
  availableQuantity: number;
  totalDiscarded: number;
  originalQuantity: number;
  unitCost?: number;
  entryDate: string;
  isReadyForDispatch: boolean;
  isActive: boolean;
  agroforestryReceiptLineId?: string;
  receiptNumber?: string;
}

export const agroforestryApi = {
  createReceipt: async (dto: CreateAgroforestryReceiptDto): Promise<AgroforestryReceiptDto> => {
    const { data } = await apiClient.post<ApiResponse<AgroforestryReceiptDto>>(
      "/agroforestry/receipts",
      dto,
    );
    return data.data;
  },

  getPendingReceipts: async (): Promise<AgroforestryReceiptDto[]> => {
    const { data } = await apiClient.get<ApiResponse<AgroforestryReceiptDto[]>>(
      "/agroforestry/receipts/pending",
    );
    return data.data ?? [];
  },

  getReceipts: async (status?: ApprovalStatus): Promise<AgroforestryReceiptDto[]> => {
    const url = status !== undefined ? `/agroforestry/receipts?status=${status}` : "/agroforestry/receipts";
    const { data } = await apiClient.get<ApiResponse<AgroforestryReceiptDto[]>>(url);
    return data.data ?? [];
  },

  getReceiptById: async (id: string): Promise<AgroforestryReceiptDto> => {
    const { data } = await apiClient.get<ApiResponse<AgroforestryReceiptDto>>(
      `/agroforestry/receipts/${id}`,
    );
    return data.data;
  },

  approveReceipt: async (id: string, dto: ApproveAgroforestryReceiptDto): Promise<AgroforestryReceiptDto> => {
    const { data } = await apiClient.post<ApiResponse<AgroforestryReceiptDto>>(
      `/agroforestry/receipts/${id}/approve`,
      dto,
    );
    return data.data;
  },

  getStock: async (): Promise<AgroforestryStockEntryDto[]> => {
    const { data } = await apiClient.get<ApiResponse<AgroforestryStockEntryDto[]>>(
      "/agroforestry/stock",
    );
    return data.data ?? [];
  },

  getStockPaged: async (params: { pageNumber?: number; pageSize?: number; search?: string; status?: string }): Promise<{ items: AgroforestryStockEntryDto[]; totalCount: number; currentPage: number; pageSize: number; totalPages: number }> => {
    const { data } = await apiClient.get<ApiResponse<{ items: AgroforestryStockEntryDto[]; totalCount: number; currentPage: number; pageSize: number; totalPages: number }>>(
      "/agroforestry/stock/paged",
      { params },
    );
    return data.data;
  },

  markReadyForSale: async (entryId: string, quantity: number): Promise<AgroforestryStockEntryDto> => {
    const { data } = await apiClient.post<ApiResponse<AgroforestryStockEntryDto>>(
      `/agroforestry/stock/${entryId}/mark-ready`,
      { quantity },
    );
    return data.data;
  },
};
